import { getOfflineCache, queueMutation } from './offlineDB';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// ── Mise en file d'attente d'une mutation ─────────────────────────
// Retourne une réponse "succès offline" que les composants peuvent utiliser
async function queueAndReturn(method, endpoint, options) {
  // Extraire le body (JSON string → objet)
  let body = null;
  if (options.body) {
    try   { body = JSON.parse(options.body); }
    catch { body = options.body; }            // garde le body tel quel si non-JSON
  }

  const offlineId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  await queueMutation(method, endpoint, body, offlineId);

  return {
    id         : offlineId,
    offline    : true,
    queued     : true,
    success    : true,
    created_at : new Date().toISOString(),
    message    : 'Modification enregistrée localement — sera synchronisée dès le retour du serveur',
  };
}

// ─────────────────────────────────────────────────────────────────

export async function apiFetch(endpoint, options = {}) {
  const url    = `${API_BASE}${endpoint}`;
  const token  = localStorage.getItem('gf_token') || localStorage.getItem('gf_formateur_token');
  const method = (options.method || 'GET').toUpperCase();
  const isWrite = method !== 'GET' && method !== 'HEAD';

  const fetchOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  };

  // ── Court-circuit : si clairement hors-ligne et c'est une écriture ──
  // → mise en file immédiate sans tentative réseau
  if (isWrite && !navigator.onLine) {
    return queueAndReturn(method, endpoint, options);
  }

  // ── Tentative réseau ──────────────────────────────────────────────
  let res;
  try {
    res = await fetch(url, fetchOptions);
  } catch (networkErr) {
    // Erreur réseau (serveur inaccessible, DNS, timeout…)
    if (isWrite) {
      // Écriture : mettre en file d'attente plutôt que de perdre les données
      return queueAndReturn(method, endpoint, options);
    }
    // Lecture : essayer le cache IndexedDB
    const cached = await getOfflineCache(endpoint).catch(() => null);
    if (cached) return cached;
    throw networkErr;
  }

  if (res.status === 401) {
    localStorage.removeItem('gf_token');
    window.location.reload();
    return;
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (!res.ok) {
    if (isJson) {
      const err = await res.json().catch(() => ({ error: res.statusText }));

      // Réponse 503 émise par le Service Worker quand pas de cache réseau disponible
      if (err.offline && !isWrite) {
        const cached = await getOfflineCache(endpoint).catch(() => null);
        if (cached) return cached;
      }

      const error = new Error(err.error || 'Erreur API');
      error.status = res.status;
      error.data   = err;
      throw error;
    }
    const error = new Error(`Erreur ${res.status} : ${res.statusText}`);
    error.status = res.status;
    throw error;
  }

  if (!isJson) {
    throw new Error(`Route introuvable : ${endpoint}`);
  }

  return res.json();
}

export function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, v);
  });
  return q.toString() ? `?${q.toString()}` : '';
}
